import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSocket } from '../context/SocketContext.jsx';
import {
  getFinalScoreFromCompletionPayload,
  getMatchIdFromSocketPayload
} from '../utils/matchCompletion';

// Feature flags via Vite env (string values). Defaults to commentary-first behaviour.
const USE_COMMENTARY_SCORE_FIRST = String(import.meta.env.VITE_USE_COMMENTARY_SCORE_FIRST ?? 'true').toLowerCase() !== 'false';
const ENGINE_FALLBACK_MS = Number(import.meta.env.VITE_ENGINE_SCORE_FALLBACK_MS ?? 15000);
const COMMENTARY_SCORE_HOLD_MAX_MS = 20000;
const LIVE_SCORE_CACHE_TTL_MS = Number(import.meta.env.VITE_LIVE_SCORE_CACHE_TTL_MS ?? 30 * 60 * 1000);
const LIVE_SCORE_CACHE_MAX_ENTRIES = Number(import.meta.env.VITE_LIVE_SCORE_CACHE_MAX_ENTRIES ?? 100);
const LIVE_SCORE_CACHE_RESTORE_MAX_AGE_MS = Number(import.meta.env.VITE_LIVE_SCORE_CACHE_RESTORE_MAX_AGE_MS ?? 15 * 1000);
const liveScoreCache = new Map();

const getNormalisedPositiveInteger = (value, fallback) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed > 0 ? Math.trunc(parsed) : fallback;
};

const NORMALISED_LIVE_SCORE_CACHE_TTL_MS = getNormalisedPositiveInteger(LIVE_SCORE_CACHE_TTL_MS, 30 * 60 * 1000);
const NORMALISED_LIVE_SCORE_CACHE_MAX_ENTRIES = getNormalisedPositiveInteger(LIVE_SCORE_CACHE_MAX_ENTRIES, 100);
const NORMALISED_LIVE_SCORE_CACHE_RESTORE_MAX_AGE_MS = getNormalisedPositiveInteger(LIVE_SCORE_CACHE_RESTORE_MAX_AGE_MS, 15 * 1000);

const deleteCachedLiveScoreState = (matchId) => {
  if (!matchId) return;
  liveScoreCache.delete(matchId);
};

const pruneLiveScoreCache = (now = Date.now()) => {
  for (const [matchId, cached] of liveScoreCache.entries()) {
    const updatedAt = cached?.updatedAt;
    if (!Number.isFinite(updatedAt) || (now - updatedAt) > NORMALISED_LIVE_SCORE_CACHE_TTL_MS) {
      liveScoreCache.delete(matchId);
    }
  }
};

const enforceLiveScoreCacheLimit = () => {
  if (liveScoreCache.size <= NORMALISED_LIVE_SCORE_CACHE_MAX_ENTRIES) return;
  const sortedByAge = Array.from(liveScoreCache.entries())
    .sort((a, b) => (a[1]?.updatedAt ?? 0) - (b[1]?.updatedAt ?? 0));
  const removeCount = liveScoreCache.size - NORMALISED_LIVE_SCORE_CACHE_MAX_ENTRIES;
  for (let index = 0; index < removeCount; index += 1) {
    const matchId = sortedByAge[index]?.[0];
    if (matchId) {
      liveScoreCache.delete(matchId);
    }
  }
};

const readCachedLiveScoreState = (matchId, options = {}) => {
  if (!matchId) return null;
  pruneLiveScoreCache();
  const cached = liveScoreCache.get(matchId);
  if (!cached || typeof cached !== 'object') return null;
  const maxAgeMs = options?.maxAgeMs;
  const updatedAt = cached?.updatedAt;
  if (Number.isFinite(maxAgeMs) && maxAgeMs > 0) {
    if (!Number.isFinite(updatedAt) || (Date.now() - updatedAt) > maxAgeMs) {
      liveScoreCache.delete(matchId);
      return null;
    }
  }
  if (!cached.liveScore || typeof cached.liveScore.home !== 'number' || typeof cached.liveScore.away !== 'number') {
    liveScoreCache.delete(matchId);
    return null;
  }
  return cached;
};

const writeCachedLiveScoreState = (matchId, cachedState) => {
  if (!matchId || !cachedState) return;
  pruneLiveScoreCache();
  liveScoreCache.set(matchId, {
    ...cachedState,
    updatedAt: Date.now()
  });
  enforceLiveScoreCacheLimit();
};

export function __resetLiveScoreCacheForTests() {
  liveScoreCache.clear();
}

export function isGoalRevealingCommentaryEvent(event) {
  const eventType = typeof event?.event_type === 'string'
    ? event.event_type.toUpperCase()
    : '';

  if (eventType === 'SCORE_UPDATE') return true;
  if (!eventType.includes('GOAL')) return false;

  // The engine emits each goal narrative part as its own event with a
  // single-part narrative object (buildup first, outcome two rows later), so
  // only the part carrying the outcome text is the on-screen "GOAL!" moment.
  const hasNarrative = event?.narrative && typeof event.narrative === 'object';
  if (hasNarrative) {
    const outcomeText = event.narrative.outcome;
    return Boolean(typeof outcomeText === 'string' && outcomeText.trim());
  }
  return Boolean(event?.sub_type === 'outcome' || !event?.sub_type);
}

/**
 * useLiveScore(matchId, { isMatchFinished?: boolean })
 * - Subscribes to commentary_update (preferred), match_completed/simulation_ended, and engine_state_update (fallback)
 * - When isMatchFinished is true, live updates/cache are ignored so authoritative persisted scores can be used.
 * - Returns an object: { liveScore: { home, away } | null, hasCommentaryScore: boolean, lastUpdatedSource: 'commentary' | 'engine' | null }
 */
export function useLiveScore(matchId, options = {}) {
  const socketCtx = useSocket();
  const isMatchFinished = Boolean(options?.isMatchFinished);
  const cachedState = isMatchFinished
    ? null
    : readCachedLiveScoreState(matchId, { maxAgeMs: NORMALISED_LIVE_SCORE_CACHE_RESTORE_MAX_AGE_MS });
  const [liveScore, setLiveScore] = useState(cachedState?.liveScore ?? null); // {home, away}
  const [hasCommentaryScore, setHasCommentaryScore] = useState(cachedState?.hasCommentaryScore ?? false);
  const [lastUpdatedSource, setLastUpdatedSource] = useState(cachedState?.lastUpdatedSource ?? null); // 'commentary' | 'engine' | null

  const hasCommentaryScoreRef = useRef(cachedState?.hasCommentaryScore ?? false);
  const lastCommentaryAtRef = useRef(cachedState?.lastCommentaryAt ?? 0);
  const engineScoreRef = useRef(null); // latest engine-derived score
  const displayedLiveScoreRef = useRef(cachedState?.liveScore ?? null);
  const commentaryScoreCandidateRef = useRef(null);
  const hasAppliedInitialCommentaryScoreRef = useRef(false);
  const commentaryScoreHoldTimerRef = useRef(null);
  const mountedRef = useRef(false);

  const debugLog = useCallback((msg, extra) => {
    // Temporary debug logs to monitor ordering
    console.log(`[useLiveScore] ${msg}`, extra ?? '');
  }, []);

  const persistScoreCache = useCallback((score, source, commentarySeen = hasCommentaryScoreRef.current) => {
    if (!matchId || !score) return;
    writeCachedLiveScoreState(matchId, {
      liveScore: { home: score.home, away: score.away },
      hasCommentaryScore: Boolean(commentarySeen),
      lastUpdatedSource: source,
      lastCommentaryAt: lastCommentaryAtRef.current
    });
  }, [matchId]);

  const clearCommentaryScoreHoldTimer = useCallback(() => {
    if (commentaryScoreHoldTimerRef.current) {
      clearTimeout(commentaryScoreHoldTimerRef.current);
      commentaryScoreHoldTimerRef.current = null;
    }
  }, []);

  const applyCommentaryScore = useCallback((score) => {
    clearCommentaryScoreHoldTimer();
    displayedLiveScoreRef.current = { home: score.home, away: score.away };
    hasCommentaryScoreRef.current = true;
    lastCommentaryAtRef.current = Date.now();

    // Defer to next microtask so if engine updated in same JS turn, commentary wins in UI ordering
    queueMicrotask(() => {
      setLiveScore(prev => {
        if (prev && prev.home === score.home && prev.away === score.away) return prev;
        return { home: score.home, away: score.away };
      });
      setHasCommentaryScore(true);
      setLastUpdatedSource('commentary');
      persistScoreCache(score, 'commentary', true);
      debugLog('Applied commentary score', score);
    });
  }, [clearCommentaryScoreHoldTimer, debugLog, persistScoreCache]);

  const holdCommentaryScore = useCallback(() => {
    hasCommentaryScoreRef.current = true;
    lastCommentaryAtRef.current = Date.now();
    setHasCommentaryScore(true);

    if (commentaryScoreHoldTimerRef.current) return;
    commentaryScoreHoldTimerRef.current = setTimeout(() => {
      commentaryScoreHoldTimerRef.current = null;
      const heldScore = commentaryScoreCandidateRef.current;
      if (heldScore) {
        applyCommentaryScore(heldScore);
      }
    }, COMMENTARY_SCORE_HOLD_MAX_MS);
  }, [applyCommentaryScore]);

  const applyEngineScoreIfAllowed = useCallback((score) => {
    if (!score) return;
    const now = Date.now();

    // If commentary-first is disabled, always apply engine immediately
    if (!USE_COMMENTARY_SCORE_FIRST) {
      displayedLiveScoreRef.current = { ...score };
      setLiveScore(prev => (prev && prev.home === score.home && prev.away === score.away) ? prev : { ...score });
      setLastUpdatedSource('engine');
      persistScoreCache(score, 'engine');
      debugLog('Applied engine score (flag disabled)', score);
      return;
    }

    const noCommentarySeenYet = !hasCommentaryScoreRef.current;
    const commentaryStale = (now - lastCommentaryAtRef.current) > ENGINE_FALLBACK_MS;

    if (noCommentarySeenYet || commentaryStale) {
      displayedLiveScoreRef.current = { ...score };
      setLiveScore(prev => (prev && prev.home === score.home && prev.away === score.away) ? prev : { ...score });
      setLastUpdatedSource('engine');
      persistScoreCache(score, 'engine');
      debugLog('Applied engine score (fallback)', { score, noCommentarySeenYet, commentaryStale });
    }
  }, [debugLog, persistScoreCache]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  useEffect(() => {
    hasCommentaryScoreRef.current = hasCommentaryScore;
  }, [hasCommentaryScore]);

  useEffect(() => {
    clearCommentaryScoreHoldTimer();
    commentaryScoreCandidateRef.current = null;
    hasAppliedInitialCommentaryScoreRef.current = false;

    if (isMatchFinished) {
      deleteCachedLiveScoreState(matchId);
      setLiveScore(null);
      setHasCommentaryScore(false);
      setLastUpdatedSource(null);
      hasCommentaryScoreRef.current = false;
      lastCommentaryAtRef.current = 0;
      engineScoreRef.current = null;
      displayedLiveScoreRef.current = null;
      return;
    }

    const cached = readCachedLiveScoreState(matchId, { maxAgeMs: NORMALISED_LIVE_SCORE_CACHE_RESTORE_MAX_AGE_MS });
    if (cached) {
      setLiveScore(cached.liveScore);
      setHasCommentaryScore(Boolean(cached.hasCommentaryScore));
      setLastUpdatedSource(cached.lastUpdatedSource ?? null);
      hasCommentaryScoreRef.current = Boolean(cached.hasCommentaryScore);
      lastCommentaryAtRef.current = cached.lastCommentaryAt ?? 0;
      displayedLiveScoreRef.current = cached.liveScore;
      return;
    }
    setLiveScore(null);
    setHasCommentaryScore(false);
    setLastUpdatedSource(null);
    hasCommentaryScoreRef.current = false;
    lastCommentaryAtRef.current = 0;
    engineScoreRef.current = null;
    displayedLiveScoreRef.current = null;
  }, [clearCommentaryScoreHoldTimer, matchId, isMatchFinished]);

  useEffect(() => {
    if (!socketCtx || !matchId || isMatchFinished) return;

    hasAppliedInitialCommentaryScoreRef.current = false;
    commentaryScoreCandidateRef.current = null;
    clearCommentaryScoreHoldTimer();

    const handleCompletionScore = (payload, eventName) => {
      try {
        const pid = getMatchIdFromSocketPayload(payload);
        if (!pid || pid !== matchId) return;
        clearCommentaryScoreHoldTimer();
        commentaryScoreCandidateRef.current = null;
        const finalScore = getFinalScoreFromCompletionPayload(payload);
        if (finalScore) {
          displayedLiveScoreRef.current = { home: finalScore.home, away: finalScore.away };
          setLiveScore({ home: finalScore.home, away: finalScore.away });
          setLastUpdatedSource('engine'); // treat as final authoritative source
          debugLog(`Applied final score from ${eventName}`, finalScore);
        }
        deleteCachedLiveScoreState(matchId);
      } catch (e) {
        debugLog(`Error processing ${eventName}`, e);
      }
    };

    const unsubCommentary = socketCtx.subscribe('commentary_update', (payload) => {
      try {
        const pid = getMatchIdFromSocketPayload(payload);
        if (!pid || pid !== matchId) return;
        const score = payload?.score;
        const hasValidScore = score &&
          typeof score.home === 'number' &&
          typeof score.away === 'number';

        if (hasValidScore) {
          commentaryScoreCandidateRef.current = { home: score.home, away: score.away };
        }

        const candidateScore = commentaryScoreCandidateRef.current;
        if (!candidateScore) return;

        if (!hasAppliedInitialCommentaryScoreRef.current) {
          hasAppliedInitialCommentaryScoreRef.current = true;
          applyCommentaryScore(candidateScore);
          return;
        }

        const displayedScore = displayedLiveScoreRef.current;
        const matchesDisplayedScore = displayedScore &&
          displayedScore.home === candidateScore.home &&
          displayedScore.away === candidateScore.away;

        if (matchesDisplayedScore || isGoalRevealingCommentaryEvent(payload)) {
          applyCommentaryScore(candidateScore);
          return;
        }

        // The engine stamps ahead-of-stream scores, so non-goal commentary must
        // not move the displayed score before the goal narrative is shown.
        holdCommentaryScore();
      } catch (e) {
        debugLog('Error processing commentary_update', e);
      }
    });

    const unsubEngine = socketCtx.subscribe('engine_state_update', (payload) => {
      try {
        const pid = getMatchIdFromSocketPayload(payload);
        if (!pid || pid !== matchId) return;
        const home = payload?.home_team?.score;
        const away = payload?.away_team?.score;
        if (typeof home !== 'number' || typeof away !== 'number') return;
        engineScoreRef.current = { home, away };
        applyEngineScoreIfAllowed(engineScoreRef.current);
      } catch (e) {
        debugLog('Error processing engine_state_update', e);
      }
    });

    const unsubEnded = socketCtx.subscribe('simulation_ended', (payload) => {
      handleCompletionScore(payload, 'simulation_ended');
    });

    const unsubMatchCompleted = socketCtx.subscribe('match_completed', (payload) => {
      handleCompletionScore(payload, 'match_completed');
    });

    return () => {
      if (typeof unsubCommentary === 'function') unsubCommentary();
      if (typeof unsubEngine === 'function') unsubEngine();
      if (typeof unsubEnded === 'function') unsubEnded();
      if (typeof unsubMatchCompleted === 'function') unsubMatchCompleted();
      clearCommentaryScoreHoldTimer();
      commentaryScoreCandidateRef.current = null;
      hasAppliedInitialCommentaryScoreRef.current = false;
    };
  }, [
    applyCommentaryScore,
    applyEngineScoreIfAllowed,
    clearCommentaryScoreHoldTimer,
    debugLog,
    holdCommentaryScore,
    socketCtx,
    matchId,
    isMatchFinished
  ]);

  return useMemo(() => ({ liveScore, hasCommentaryScore, lastUpdatedSource }), [liveScore, hasCommentaryScore, lastUpdatedSource]);
}

export default useLiveScore;
